import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, LogOut, Camera, ShieldCheck } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import api, { authStore } from "../lib/api";

/**
 * 6-Page Registration Wizard — Data Contract v1.0 (§2.1, §3).
 * Each step maps 1:1 into `users.registration.<subkey>`:
 *   1 → personal · 2 → religion · 3 → family · 4 → profile_creator
 *   5 → astrology · 6 → education (photo_b64 mandatory)
 */
const STEPS = [
  { key: "personal",        title: "Personal Details" },
  { key: "religion",        title: "Religion, Caste & Gotra" },
  { key: "family",          title: "Family Background" },
  { key: "profile_creator", title: "Profile Creator & Location" },
  { key: "astrology",       title: "Astrology & Kundali Basis" },
  { key: "education",       title: "Education, Career & Photo" },
];

const Field = ({ label, required, children }) => (
  <div>
    <label className="input-label">
      {label}{required && <span style={{ color: "var(--crimson)" }}> *</span>}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, ...rest }) => (
  <input value={value ?? ""} onChange={onChange} className="underline-input" {...rest} />
);

const Select = ({ value, onChange, options, ...rest }) => (
  <select value={value ?? ""} onChange={onChange} className="underline-input" {...rest}>
    <option value="" disabled>Select…</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const TextArea = ({ value, onChange, ...rest }) => (
  <textarea value={value ?? ""} onChange={onChange} rows={3} className="underline-input" style={{ resize: "vertical" }} {...rest}/>
);

export default function RegisterWizard() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialStep = Math.min(Math.max(parseInt(params.get("step") || "1", 10), 1), STEPS.length);
  const [step, setStep] = useState(initialStep);
  // Nested registration state: { personal:{...}, religion:{...}, ... }
  const [reg, setReg] = useState(() => Object.fromEntries(STEPS.map((s) => [s.key, {}])));
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!authStore.token()) {
      toast.error("Please verify your mobile first");
      nav("/");
      return;
    }
    (async () => {
      try {
        const { data: state } = await api.get("/wizard/state");
        if (state.is_profile_complete) { nav("/home"); return; }
        const server = state.registration || {};
        setReg((prev) => ({
          ...prev,
          ...Object.fromEntries(STEPS.map((s) => [s.key, server[s.key] || {}])),
        }));
        const resume = Math.min((state.registration_step || 0) + 1, STEPS.length);
        if (!params.get("step")) setStep(resume);
      } catch (err) {
        if (err?.response?.status === 401) {
          authStore.clear();
          toast.error("Session expired");
          nav("/");
        }
      } finally { setLoaded(true); }
    })();
  }, []);

  const currentKey = STEPS[step - 1].key;
  const cur = reg[currentKey] || {};
  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setReg((r) => ({ ...r, [currentKey]: { ...r[currentKey], [k]: v } }));
  };

  const setPhotoFromFile = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Photo must be under 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setReg((r) => ({ ...r, education: { ...r.education, photo_b64: reader.result } }));
    };
    reader.readAsDataURL(file);
  };

  const gotoStep = (n) => { setStep(n); setParams({ step: String(n) }); };

  const validateStep = () => {
    if (step === 1) {
      if (!cur.name) return "Full name is required";
      if (!cur.gender) return "Gender is required";
      if (!cur.dob) return "Date of birth is required";
    }
    if (step === 2) {
      if (!cur.caste) return "Caste / community is required";
      if (!cur.gotra) return "Gotra is required";
    }
    if (step === 4) {
      if (!cur.profile_creator_relation) return "Who is creating this profile?";
      if (!cur.city) return "City is required";
    }
    if (step === 6) {
      if (!cur.education) return "Highest education is required";
      if (!cur.photo_b64) return "A profile photo is required to complete registration";
    }
    return null;
  };

  const saveAndNext = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/wizard/step", { step, data: reg[currentKey] || {} });
      if (res.is_profile_complete) {
        toast.success("Profile submitted successfully");
        nav("/home");
      } else {
        toast.success(`Step ${step} saved`);
        gotoStep(res.next_step || Math.min(step + 1, STEPS.length));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const logout = () => { authStore.clear(); nav("/"); };

  const photoPreview = useMemo(() => reg.education?.photo_b64 || null, [reg.education]);

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ivory)" }}>
        <p className="eyebrow">Loading…</p>
      </div>
    );
  }

  return (
    <div data-testid="register-wizard" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <BrandLogo size="sm"/>
        <button onClick={logout} data-testid="wizard-logout" className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
          <LogOut size={14}/> Exit
        </button>
      </header>

      <section className="px-6 md:px-10 pt-8 pb-2">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-1.5 mb-4" data-testid="progress-bar">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex-1 h-1" style={{ background: i + 1 <= step ? "var(--crimson)" : "var(--line)" }}/>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
              Step {step} of {STEPS.length}
            </span>
            <span className="font-serif text-2xl md:text-3xl" style={{ color: "var(--crimson)" }} data-testid="step-title">
              {STEPS[step - 1].title}
            </span>
          </div>
        </div>
      </section>

      <section className="flex-1 px-6 md:px-10 pt-8 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-5" data-testid={`step-${currentKey}`}>

            {step === 1 && (
              <>
                <Field label="Full Name" required>
                  <Input data-testid="f-name" value={cur.name} onChange={setField("name")} placeholder="e.g., Priya Sharma"/>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Gender" required>
                    <Select data-testid="f-gender" value={cur.gender} onChange={setField("gender")} options={["Male", "Female", "Other"]}/>
                  </Field>
                  <Field label="Date of Birth" required>
                    <Input data-testid="f-dob" type="date" value={cur.dob} onChange={setField("dob")}/>
                  </Field>
                  <Field label="Marital Status">
                    <Select value={cur.marital_status} onChange={setField("marital_status")} options={["Never Married", "Divorced", "Widowed", "Awaiting Divorce"]}/>
                  </Field>
                  <Field label="Height (cm)">
                    <Input data-testid="f-height" type="number" min={120} max={220} value={cur.height_cm} onChange={setField("height_cm")}/>
                  </Field>
                  <Field label="Mother Tongue">
                    <Input value={cur.mother_tongue} onChange={setField("mother_tongue")} placeholder="Hindi / Bhojpuri"/>
                  </Field>
                  <Field label="Complexion">
                    <Select value={cur.complexion} onChange={setField("complexion")} options={["Fair", "Wheatish", "Dusky", "Dark"]}/>
                  </Field>
                  <Field label="Body Type">
                    <Select value={cur.body_type} onChange={setField("body_type")} options={["Slim", "Average", "Athletic", "Heavy"]}/>
                  </Field>
                </div>
              </>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Religion">
                  <Input value={cur.religion || "Hindu"} onChange={setField("religion")}/>
                </Field>
                <Field label="Caste / Community" required>
                  <Input data-testid="f-caste" value={cur.caste} onChange={setField("caste")} placeholder="e.g., Brahmin"/>
                </Field>
                <Field label="Sub-caste">
                  <Input value={cur.sub_caste} onChange={setField("sub_caste")}/>
                </Field>
                <Field label="Gotra" required>
                  <Input data-testid="f-gotra" value={cur.gotra} onChange={setField("gotra")} placeholder="e.g., Kashyap"/>
                </Field>
                <Field label="Kuldevi (Family Deity)">
                  <Input value={cur.kuldevi} onChange={setField("kuldevi")}/>
                </Field>
                <Field label="Kuldevta">
                  <Input value={cur.kuldevta} onChange={setField("kuldevta")}/>
                </Field>
                <Field label="Manglik Status">
                  <Select value={cur.manglik} onChange={setField("manglik")} options={["No", "Yes", "Anshik (Partial)", "Not Sure"]}/>
                </Field>
                <Field label="Bhakoot (Family welfare)">
                  <Input value={cur.bhakoot} onChange={setField("bhakoot")}/>
                </Field>
              </div>
            )}

            {step === 3 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Father's Name">
                    <Input data-testid="f-father-name" value={cur.father_name} onChange={setField("father_name")}/>
                  </Field>
                  <Field label="Father's Occupation">
                    <Input value={cur.father_occupation} onChange={setField("father_occupation")}/>
                  </Field>
                  <Field label="Mother's Name">
                    <Input value={cur.mother_name} onChange={setField("mother_name")}/>
                  </Field>
                  <Field label="Mother's Occupation">
                    <Input value={cur.mother_occupation} onChange={setField("mother_occupation")}/>
                  </Field>
                  <Field label="Siblings">
                    <Input value={cur.siblings} onChange={setField("siblings")} placeholder="e.g., 1 brother, 1 sister"/>
                  </Field>
                  <Field label="Family Type">
                    <Select value={cur.family_type} onChange={setField("family_type")} options={["Nuclear", "Joint"]}/>
                  </Field>
                  <Field label="Family Values">
                    <Select value={cur.family_values} onChange={setField("family_values")} options={["Traditional", "Moderate", "Liberal"]}/>
                  </Field>
                  <Field label="Family Status">
                    <Select value={cur.family_status} onChange={setField("family_status")} options={["Middle Class", "Upper Middle Class", "Affluent"]}/>
                  </Field>
                  <Field label="Diet">
                    <Select value={cur.diet} onChange={setField("diet")} options={["Vegetarian", "Non-Vegetarian", "Jain / Sattvic", "Eggetarian"]}/>
                  </Field>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Profile Created By" required>
                    <Select data-testid="f-creator-relation" value={cur.profile_creator_relation} onChange={setField("profile_creator_relation")}
                      options={["Self", "Father", "Mother", "Brother", "Sister", "Family Friend", "Relative"]}/>
                  </Field>
                  <Field label="Creator Name (if different)">
                    <Input value={cur.creator_name} onChange={setField("creator_name")}/>
                  </Field>
                  <Field label="City" required>
                    <Input data-testid="f-city" value={cur.city} onChange={setField("city")}/>
                  </Field>
                  <Field label="State">
                    <Input value={cur.state} onChange={setField("state")}/>
                  </Field>
                  <Field label="Country">
                    <Input value={cur.country || "India"} onChange={setField("country")}/>
                  </Field>
                  <Field label="Native Village / Town">
                    <Input value={cur.native_village} onChange={setField("native_village")}/>
                  </Field>
                </div>
              </>
            )}

            {step === 5 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Rashi (Moon Sign)">
                    <Input value={cur.rashi} onChange={setField("rashi")}/>
                  </Field>
                  <Field label="Nakshatra">
                    <Input value={cur.nakshatra} onChange={setField("nakshatra")}/>
                  </Field>
                  <Field label="Nakshatra Pada (1–4)">
                    <Input type="number" min={1} max={4} value={cur.nakshatra_pada} onChange={setField("nakshatra_pada")}/>
                  </Field>
                  <Field label="Manglik Status">
                    <Select value={cur.manglik} onChange={setField("manglik")} options={["No", "Yes", "Anshik (Partial)", "Not Sure"]}/>
                  </Field>
                  <Field label="Time of Birth (HH:MM)">
                    <Input data-testid="f-birth-time" type="time" value={cur.birth_time} onChange={setField("birth_time")}/>
                  </Field>
                  <Field label="Place of Birth">
                    <Input data-testid="f-birth-place" value={cur.birth_place} onChange={setField("birth_place")} placeholder="City, State"/>
                  </Field>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: "var(--ash)" }}>
                  Detailed Kundali PDF is generated on-demand via Prokerala from these values.
                </p>
              </>
            )}

            {step === 6 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Highest Education" required>
                    <Input data-testid="f-education" value={cur.education} onChange={setField("education")} placeholder="B.Tech / MBA / MBBS"/>
                  </Field>
                  <Field label="Occupation">
                    <Input value={cur.profession} onChange={setField("profession")}/>
                  </Field>
                  <Field label="Annual Income (₹)">
                    <Input data-testid="f-income" value={cur.annual_income} onChange={setField("annual_income")} placeholder="e.g., 6,00,000"/>
                  </Field>
                  <Field label="About Yourself">
                    <TextArea value={cur.about} onChange={setField("about")} placeholder="Two-three lines about you and your values."/>
                  </Field>
                </div>

                <div className="p-6 mt-4" style={{ background: "var(--bone)", border: "1px solid var(--gold)" }}>
                  <div className="flex items-center gap-2">
                    <Camera size={16} style={{ color: "var(--crimson)" }}/>
                    <p className="font-serif text-lg" style={{ color: "var(--crimson)" }}>Profile Photo (Required)</p>
                  </div>
                  <p className="mt-2 text-sm" style={{ color: "var(--ash)" }}>
                    Upload a clear, recent photo of yourself. This is mandatory per verification policy.
                  </p>
                  <div className="mt-4 flex items-center gap-4">
                    {photoPreview ? (
                      <img data-testid="photo-preview" src={photoPreview} alt="Preview"
                        className="w-24 h-24 object-cover" style={{ border: "1px solid var(--gold)" }}/>
                    ) : (
                      <div className="w-24 h-24 flex items-center justify-center"
                        style={{ border: "1px dashed var(--ash)", color: "var(--ash)" }}>
                        <Camera size={20}/>
                      </div>
                    )}
                    <div>
                      <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                        data-testid="f-photo" onChange={(e) => setPhotoFromFile(e.target.files?.[0])}/>
                      <button type="button" onClick={() => photoInputRef.current?.click()} className="btn-primary text-[11px] py-2 px-3">
                        {photoPreview ? "Change photo" : "Upload photo"}
                      </button>
                      <p className="mt-2 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>
                        Under 3 MB · JPG/PNG
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 p-4 flex items-start gap-2" style={{ background: "rgba(85,127,74,0.06)", border: "1px solid var(--leaf)" }}>
                  <ShieldCheck size={14} style={{ color: "var(--leaf)", marginTop: 2 }}/>
                  <p className="text-[11px]" style={{ color: "var(--ink)" }}>
                    Aadhaar OKYC is a separate identity gate (unlocked from the Home hub) — not part of the 6-step wizard.
                  </p>
                </div>

                <p className="mt-5 text-[11px] tracking-widest uppercase text-center" style={{ color: "var(--ash)" }}>
                  By submitting you confirm all details are true and agree to our Terms &amp; Privacy Policy.
                </p>
              </>
            )}
          </div>

          <div className="flex items-center justify-between mt-10 pt-6" style={{ borderTop: "1px solid var(--line)" }}>
            <button data-testid="wizard-back" onClick={() => step > 1 && gotoStep(step - 1)} disabled={step === 1}
              className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
              style={{ color: step === 1 ? "var(--line)" : "var(--ash)", cursor: step === 1 ? "not-allowed" : "pointer" }}>
              <ArrowLeft size={14}/> Back
            </button>
            <button data-testid="wizard-next" onClick={saveAndNext} disabled={busy} className="btn-primary">
              {busy ? "Saving…" : (step === STEPS.length ? "Submit Profile" : "Save & Continue")}
              {step === STEPS.length ? <Check size={14}/> : <ArrowRight size={14}/>}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
